/**
 * Exchange Fee Structure
 * Manages trading fees for all supported exchanges
 */

export type QuoteCurrency = 'USDT' | 'BTC' | 'KRW';

interface ExchangeFees {
  maker: { USDT: number; BTC: number; KRW: number | null; };
  taker: { USDT: number; BTC: number; KRW: number | null; };
}

export class FeeStructure {
  /**
   * Custom fee overrides from user settings
   */
  private static customFees: Record<string, ExchangeFees> = {};

  /**
   * Static fee data for all exchanges
   * Values are in decimal form (0.001 = 0.1%)
   * null means the exchange doesn't support that quote currency
   */
  private static readonly defaultFees: Record<string, ExchangeFees> = {
    'Upbit': {
      maker: { USDT: 0.0025, BTC: 0.0025, KRW: 0.0005 },
      taker: { USDT: 0.0025, BTC: 0.0025, KRW: 0.0005 }
    },
    'Bithumb': {
      maker: { USDT: 0.0015, BTC: 0, KRW: 0.0004 },
      taker: { USDT: 0.0015, BTC: 0, KRW: 0.0004 }  // Updated: KRW taker from 0.25% to 0.04%
    },
    'Coinone': {
      maker: { USDT: 0.0020, BTC: 0.0020, KRW: 0.0020 },
      taker: { USDT: 0.0020, BTC: 0.0020, KRW: 0.0020 }
    },
    'Binance': {
      maker: { USDT: 0.0010, BTC: 0.0010, KRW: null },
      taker: { USDT: 0.0010, BTC: 0.0010, KRW: null }
    },
    'HTX': {
      maker: { USDT: 0.0020, BTC: 0.0020, KRW: null },
      taker: { USDT: 0.0020, BTC: 0.0020, KRW: null }
    },
    'GateIO': {
      maker: { USDT: 0.0010, BTC: 0.0010, KRW: null },  // Updated: from 0.20% to 0.10%
      taker: { USDT: 0.0010, BTC: 0.0010, KRW: null }   // Updated: from 0.20% to 0.10%
    },
    'KuCoin': {
      maker: { USDT: 0.0010, BTC: 0.0010, KRW: null },
      taker: { USDT: 0.0010, BTC: 0.0010, KRW: null }
    },
    'OKX': {
      maker: { USDT: 0.0008, BTC: 0.0008, KRW: null },
      taker: { USDT: 0.0010, BTC: 0.0010, KRW: null }
    },
    'MEXC': {
      maker: { USDT: 0, BTC: 0, KRW: null },
      taker: { USDT: 0.0005, BTC: 0.0005, KRW: null }  // Updated: from 0.02% to 0.05%
    },
    'Bybit': {
      maker: { USDT: 0.0015, BTC: 0.0010, KRW: null },  // Updated: USDT 0.15%, BTC 0.10%
      taker: { USDT: 0.0020, BTC: 0.0010, KRW: null }   // Updated: USDT 0.20%, BTC 0.10%
    },
    'Bitget': {
      maker: { USDT: 0.0010, BTC: 0.0010, KRW: null },
      taker: { USDT: 0.0010, BTC: 0.0010, KRW: null }
    }
  };

  /**
   * Set custom fee overrides
   * @param overrides Custom fee overrides from settings
   */
  static setCustomFees(overrides?: Record<string, any>) {
    this.customFees = {};
    if (overrides) {
      for (const [exchange, fees] of Object.entries(overrides)) {
        this.customFees[exchange] = fees as ExchangeFees;
      }
    }
  }

  /**
   * Get the trading fee for a specific exchange, order type, and quote currency
   * @param exchange Exchange name (e.g., 'Binance', 'Upbit')
   * @param orderType 'maker' or 'taker'
   * @param quote Quote currency ('USDT', 'BTC', 'KRW')
   * @returns Fee as a decimal (0.001 = 0.1%), defaults to 0.001 if not found
   */
  static getFee(exchange: string, orderType: 'maker' | 'taker', quote: string): number {
    // First check custom fees
    const customExchangeFees = this.customFees[exchange];
    if (customExchangeFees) {
      const quoteCurrency = quote as keyof typeof customExchangeFees.maker;
      const customFee = customExchangeFees[orderType]?.[quoteCurrency];
      if (customFee !== undefined && customFee !== null) {
        return customFee;
      }
    }

    // Fall back to default fees
    const exchangeFees = this.defaultFees[exchange];
    if (!exchangeFees) {
      console.warn(`Exchange ${exchange} not found in fee structure, using default 0.1%`);
      return 0.001;
    }
    
    const quoteCurrency = quote as keyof typeof exchangeFees.maker;
    const fee = exchangeFees[orderType][quoteCurrency];
    
    if (fee === null || fee === undefined) {
      console.warn(`${exchange} does not support ${quote} market, using default 0.1%`);
      return 0.001;
    }
    
    return fee;
  }

  /**
   * Get a formatted fee percentage string for display
   * @param fee Fee as decimal (e.g., 0.001)
   * @returns Formatted string (e.g., "0.10%")
   */
  static formatFeePercent(fee: number): string {
    return `${(fee * 100).toFixed(2)}%`;
  }

  /**
   * Check if an exchange supports a specific quote currency
   * @param exchange Exchange name
   * @param quote Quote currency
   * @returns true if supported, false otherwise
   */
  static supportsQuote(exchange: string, quote: string): boolean {
    const exchangeFees = this.defaultFees[exchange];
    if (!exchangeFees) return false;
    
    const quoteCurrency = quote as keyof typeof exchangeFees.maker;
    return exchangeFees.maker[quoteCurrency] !== null;
  }

  /**
   * Get default fees for an exchange
   */
  static getDefaultFees(exchange: string): ExchangeFees | undefined {
    return this.defaultFees[exchange];
  }
}